<template>
    <div
        class="dropdown-trigger focus-outline-only"
        @click.stop="toggleDropdown"
        @keydown.space.prevent="toggleDropdown"
        @keydown.enter.prevent="handleEnter"
        @keydown.esc="closeDropdown"
        @keydown.down.prevent="highlightNextOption"
        @keydown.up.prevent="highlightPreviousOption"
        @keydown.tab="closeDropdown"
        ref="dropdown"
        tabindex="0"
        role="combobox"
        :aria-expanded="isOpen"
    >
        <div class="dropdown-trigger-content">
            <div class="dropdown-trigger-left">
                <component :is="prefixIcon" v-if="prefixIcon" />
                <span :style="selectedOption?.style">{{ selected || placeholder }}</span>
            </div>
            <arrow-drop-down-icon class="arrow-drop-down-icon" :class="{ 'rotate': isOpen }" />
        </div>
        <teleport :to="teleportTarget" :disabled="!teleportTarget">
            <transition name="fade">
                <div
                    v-if="isOpen"
                    class="dropdown-overlay"
                    @click="closeDropdown"
                >
                    <div
                        class="dropdown-menu-container"
                        :style="{
                            top: `${menuTop}px`,
                            left: `${menuLeft}px`,
                            width: `${menuWidth}px`,
                            zIndex: 100001
                        }"
                        @click.stop
                    >
                        <div class="dropdown-menu-wrapper">
                            <div class="dropdown-menu-scroll">
                                <div
                                    v-for="(option, index) in options"
                                    :key="option.value"
                                    class="dropdown-menu-option font-sans"
                                    :class="{
                                        'highlighted': highlightedIndex === index,
                                        'selected': selected === (typeof option.label === 'function' ? '' : option.label)
                                    }"
                                    :style="typeof option.label === 'function' ? {} : option.style"
                                    @click.stop="selectOption(option)"
                                    @mouseover="highlightedIndex = index"
                                    role="option"
                                    :aria-selected="selected === option.label"
                                >
                                    <component
                                        :is="typeof option.label === 'function'
                                            ? option.label()
                                            : 'span'"
                                        v-if="typeof option.label === 'function'"
                                    />
                                    <template v-else>{{ option.label }}</template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </teleport>
    </div>
</template>

<script>
    import ArrowDropDownIcon from '../../icons/ArrowDropDownIcon.vue';
    import FontIcon from '../../icons/FontIcon.vue';

    export default {
        name: 'DropDown',

        components: {
            'arrow-drop-down-icon': ArrowDropDownIcon,
            FontIcon,
        },

        props: {
            options: {
                type: Array,
                required: true,
                default: () => [],
            },
            placeholder: {
                type: String,
                default: 'Select an option',
            },
            prefixIcon: {
                type: String,
                default: '',
            },
            selectedValue: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                isOpen: false,
                selected: '',
                highlightedIndex: -1,
                shouldOpenUpward: false,
                menuTop: 0,
                menuBottom: 0,
                menuLeft: 0,
                menuWidth: 0,
                teleportTarget: null,
            };
        },

        computed: {
            selectedOption() {
                return this.options.find((option) => option.value === this.selectedValue);
            },

            dropdownPosition() {
                return this.shouldOpenUpward ? 'dropdown-upward' : 'dropdown-downward';
            },
        },

        watch: {
            options: {
                handler() {
                    this.updateSelected();
                },
                deep: true,
            },

            selectedValue() {
                this.updateSelected();
            },
        },

        methods: {
            updateSelected() {
                this.selected = this.options.find((x) => x.value === this.selectedValue)?.label;
            },

            toggleDropdown(event) {
                if (event) {
                    event.stopPropagation();
                }

                this.checkPosition();

                this.isOpen = !this.isOpen;
                if (this.isOpen) {
                    this.highlightedIndex = -1;
                    this.updateMenuPosition();
                }
            },

            findRootElement() {
                let element = this.$refs.dropdown;
                while (element && element.parentElement) {
                    if (element.parentElement.id === 'cz3' || element.parentElement.tagName === 'BODY') {
                        return element.parentElement;
                    }
                    element = element.parentElement;
                }
                return document.body; // fallback
            },

            updateMenuPosition() {
                if (!this.$refs.dropdown) { return; }

                const rootElement = this.findRootElement();
                const rootRect = rootElement.getBoundingClientRect();
                const rect = this.$refs.dropdown.getBoundingClientRect();

                this.menuLeft = rect.left - rootRect.left;
                this.menuWidth = rect.width;

                const menuHeight = 250;
                const marginTop = 0;

                if (this.shouldOpenUpward) {
                    this.menuTop = rect.top - rootRect.top - menuHeight + 32;
                } else {
                    this.menuTop = rect.bottom - rootRect.top + marginTop;
                }
            },

            checkPosition() {
                if (!this.$refs.dropdown) { return; }

                const dropdownRect = this.$refs.dropdown.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const dropdownBottomOffset = dropdownRect.bottom;
                const menuHeight = 250;
                const marginTop = 0;
                const requiredSpace = menuHeight + marginTop;

                const spaceBelow = viewportHeight - dropdownBottomOffset;
                this.shouldOpenUpward = spaceBelow < requiredSpace;
            },

            closeDropdown() {
                this.isOpen = false;
                this.highlightedIndex = -1;
            },

            handleEnter() {
                if (this.isOpen && this.highlightedIndex >= 0) {
                    this.selectOption(this.options[this.highlightedIndex]);
                } else {
                    this.toggleDropdown();
                }
            },

            highlightNextOption() {
                if (!this.isOpen) {
                    this.isOpen = true;
                    this.highlightedIndex = 0;
                    return;
                }
                if (this.highlightedIndex >= this.options.length - 1) {
                    this.highlightedIndex = 0;
                } else {
                    this.highlightedIndex += 1;
                }

                this.scrollToHighlighted();
            },

            highlightPreviousOption() {
                if (!this.isOpen) {
                    this.isOpen = true;
                    this.highlightedIndex = this.options.length - 1;
                    return;
                }
                if (this.highlightedIndex <= 0) {
                    this.highlightedIndex = this.options.length - 1;
                } else {
                    this.highlightedIndex -= 1;
                }
                this.scrollToHighlighted();
            },

            scrollToHighlighted() {
                this.$nextTick(() => {
                    const rootElement = this.findRootElement();
                    const highlighted = rootElement.querySelector('.highlighted');
                    if (highlighted) {
                        highlighted.scrollIntoView({
                            block: 'nearest',
                            behavior: 'smooth',
                        });
                    }
                });
            },

            selectOption(option) {
                this.selected = option.label;
                this.$emit('select', option.value);
                this.closeDropdown();
            },

            handleClickOutside(event) {
                const rootElement = this.findRootElement();
                if (!rootElement.contains(event.target)) {
                    this.closeDropdown();
                }
            },
        },

        mounted() {
            document.addEventListener('click', this.handleClickOutside);
            window.addEventListener('resize', this.updateMenuPosition);
            this.selected = this.options.find((x) => x.value === this.selectedValue)?.label;

            this.$nextTick(() => {
                const rootElement = this.findRootElement();
                if (rootElement) {
                    this.teleportTarget = rootElement;
                }
            });
        },

        beforeUnmount() {
            document.removeEventListener('click', this.handleClickOutside);
            window.removeEventListener('resize', this.updateMenuPosition);
        },
    };
</script>

<style scoped>
    .dropdown-trigger {
        position: relative;
        width: 100%;
        background-color: #F5F5F5;
        height: 67px;
        border-radius: 0.25rem;
    }

    .dropdown-trigger-content {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 24px;
        height: 100%;
    }

    .dropdown-trigger-left {
        display: flex;
        align-items: center;
        gap: 16px;
    }

    .arrow-drop-down-icon {
        transition: transform 0.2s ease;
    }

    .rotate {
        transform: rotate(180deg);
    }
</style>

<style>
    .dropdown-overlay {
        position: fixed;
        inset: 0;
        background-color: rgba(0, 0, 0, 0);
        display: flex;
        z-index: 100001;
    }

    .dropdown-menu-container {
        position: fixed;
    }

    .dropdown-menu-wrapper {
        margin-top: 16px;
        width: 100%;
        background-color: #F5F5F5;
        border-radius: 0.25rem;
        padding: 8px 16px;
    }

    .dropdown-menu-scroll {
        max-height: 250px;
        overflow-y: auto;
    }

    .dropdown-menu-option {
        display: flex;
        align-items: center;
        margin: 8px 0;
        padding: 12px 16px;
        cursor: pointer;
        border-radius: 0.25rem;
    }

    .dropdown-menu-option:hover,
    .dropdown-menu-option.highlighted {
        background-color: #E6E6E6;
    }

    .dropdown-menu-option.selected,
    .dropdown-menu-option.selected:hover {
        background-color: rgba(0, 0, 0, 0.2) !important;
    }
</style>
